/* eslint-disable react/prop-types */
import { Divider } from '@material-ui/core'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'

import {
  enrolmentCardEventList,
  enrolmentCardProgramList,
  enrolmentCardTrialList,
  enrolmentCardVisibilityList
} from 'app/pages/Dashboard/ParentDashboard/constants'
import NotFound from 'assets/images/not_found.svg'

import * as styles from './styles'

import { NotFoundBlock } from '../../../../../components/NotFoundBlock'
import { TitleDescription } from '../../../../../components/TitleDescription'

// Registered here rather than inherited from the Attendance card, which is
// where this file's existing isSameOrBefore call has been getting them from.
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

export const Enrolments = (props) => {
  const { enrolmentData } = props
  const [enrolmentBlocks, setEnrolmentBlocks] = useState([])

  const getCount = (type, status, time) => {
    let count = 0
    count = enrolmentData?.filter((el) => {
      // el.status is the display label, "active (paused from 1st Dec)" once a
      // change is scheduled, so count off the raw row. That row is still active
      // until the change happens.
      const rowStatus = el.enrolment?.status ?? el.status
      let isRequired = type.includes(el.type)
      if (status) {
        isRequired =
          isRequired &&
          (status === 'terminated'
            ? rowStatus === 'terminated' || rowStatus === 'expired'
            : rowStatus === status)
      }

      if (time === 'ongoing') {
        isRequired =
          isRequired &&
          dayjs(
            type.every((v) => enrolmentCardVisibilityList.includes(v))
              ? el.start_date
              : el.end_date
          ).isSameOrBefore(dayjs(), 'day')
      }

      // Enrolments are one row per month, so a bare status count sums every
      // month the parent has ever sat out -- 5,050 paused rows on prod against
      // the 138 actually paused right now. "Currently paused" is the row whose
      // own period contains today.
      if (time === 'current') {
        // Read the period off the raw row. getEnrolmentData copies start_date
        // and terminated_on onto el but NOT end_date, and dayjs(undefined) is
        // today -- so el.end_date here reads as "still running" for every month
        // the parent has ever sat out, which is the whole bug this guards.
        // Don't add end_date to that transformer instead: the 'ongoing' branch
        // above already reads el.end_date and would change the active count.
        const endDate = el.enrolment?.end_date
        isRequired =
          isRequired &&
          Boolean(endDate) &&
          dayjs(el.start_date).isSameOrBefore(dayjs(), 'day') &&
          dayjs(endDate).isSameOrAfter(dayjs(), 'day')
      }

      if (time === 'upcoming') {
        isRequired = isRequired && rowStatus === 'upcoming'
      }

      if (time === 'attended')
        isRequired =
          isRequired &&
          dayjs(el.start_date).add(7, 'day').isBefore(dayjs(), 'day')
      return isRequired
    }).length

    return count
  }

  useEffect(() => {
    if (!isEmpty(enrolmentData)) {
      const blocks = [
        {
          heading: 'Programs',
          ongoing: getCount(enrolmentCardProgramList, 'active', 'ongoing'),
          upcoming: getCount(enrolmentCardProgramList, 'upcoming', 'upcoming'),
          paused: getCount(enrolmentCardProgramList, 'paused', 'current'),
          // terminated: getCount(enrolmentCardProgramList, 'terminated'),
          total: getCount(enrolmentCardProgramList)
        },
        {
          heading: 'Workshops',
          ongoing: getCount(enrolmentCardEventList, 'active', 'ongoing'),
          upcoming: getCount(enrolmentCardEventList, 'upcoming', 'upcoming'),
          // terminated: getCount(enrolmentCardEventList, 'terminated'),
          attended: getCount(enrolmentCardEventList, 'active', 'attended'),
          total: getCount(enrolmentCardEventList)
        },
        {
          heading: 'Trials',
          ongoing: getCount(enrolmentCardTrialList, 'active', 'ongoing'),
          upcoming: getCount(enrolmentCardTrialList, 'upcoming', 'upcoming'),
          // terminated: getCount(enrolmentCardTrialList, 'terminated'),
          attended: getCount(enrolmentCardTrialList, 'active', 'attended'),
          total: getCount(enrolmentCardTrialList)
        }
      ]
      setEnrolmentBlocks(blocks)
    }
  }, [enrolmentData])

  return (
    <styles.Body>
      <styles.ColumnFlexGap>
        {enrolmentBlocks?.filter((el) => el.total)?.length ? (
          enrolmentBlocks?.map((block, index) => {
            return block.total ? (
              <div key={'Enrolment' + index}>
                {block.heading === 'Programs' ? (
                  <styles.ColumnFlexStart>
                    <styles.Content heading type="dark">
                      {block.heading}
                    </styles.Content>
                    <styles.RowFlexGap>
                      <TitleDescription
                        heading="active"
                        description={block.ongoing}
                      />
                      <TitleDescription
                        heading="upcoming"
                        description={block.upcoming}
                      />
                      <TitleDescription
                        heading="paused"
                        description={block.paused}
                      />
                    </styles.RowFlexGap>
                  </styles.ColumnFlexStart>
                ) : block.heading === 'Workshops' &&
                  block.ongoing + block.upcoming > 0 ? (
                  <styles.RowFlexStart>
                    <styles.Content heading>
                      {block.ongoing + block.upcoming}
                    </styles.Content>
                    <styles.Content subheading type="light">
                      active workshops
                    </styles.Content>
                  </styles.RowFlexStart>
                ) : block.heading === 'Trials' && block.upcoming > 0 ? (
                  <styles.RowFlexStart>
                    <styles.Content heading>{block.upcoming}</styles.Content>
                    <styles.Content subheading type="light">
                      upcoming trials
                    </styles.Content>
                  </styles.RowFlexStart>
                ) : null}
              </div>
            ) : block.heading === 'Programs' ? (
              <styles.ColumnFlexStart>
                <styles.Content heading type="dark">
                  {block.heading}
                </styles.Content>
                <styles.RowFlexGap>
                  <TitleDescription
                    heading="active"
                    description={block.ongoing}
                  />
                  <TitleDescription
                    heading="upcoming"
                    description={block.upcoming}
                  />
                  <TitleDescription
                    heading="paused"
                    description={block.paused}
                  />
                </styles.RowFlexGap>
              </styles.ColumnFlexStart>
            ) : null
          })
        ) : (
          <NotFoundBlock message="Oh no! No enrolments found." />
        )}
      </styles.ColumnFlexGap>
    </styles.Body>
  )
}
